import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { api, rolesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import PermissionMatrix from '../../components/PermissionMatrix';
import type { Permission } from '../../components/PermissionMatrix';
import {
  isServiceAccountsCustomRole,
  shouldShowPermissionsResourceForRole
} from '../../utils/permissionsAccess';

interface RolePermissions {
  [role: string]: Permission[];
}

const PermissionsManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [rolePermissions, setRolePermissions] = useState<RolePermissions>({});
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [availableRoles, setAvailableRoles] = useState<Array<{id: string, name: string, roleType: string}>>([]);

  // Create Role Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [creating, setCreating] = useState(false);

  // Fetch role-based permissions
  const fetchRolePermissions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/permissions/roles');
      setRolePermissions(response.data.permissions);
      setAvailableRoles(response.data.roles || []);

      // Set default selected role to the first manageable role
      if (!selectedRole && response.data.roles && response.data.roles.length > 0) {
        setSelectedRole(response.data.roles[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch role permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRolePermissions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Save role permissions
  const handleSaveRolePermissions = async (permissions: Permission[]) => {
    try {
      setSaving(true);
      await api.put(`/admin/permissions/roles/${selectedRole}`, {
        permissions
      });
      const roleName = availableRoles.find(r => r.id === selectedRole)?.name || selectedRole;
      showSuccess(`Permissions updated for ${roleName} role`);
      fetchRolePermissions();
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to save role permissions');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  // Create new custom role
  const handleCreateRole = async () => {
    if (!newRoleName.trim()) {
      showError('Role name is required');
      return;
    }

    try {
      setCreating(true);
      const response = await rolesAPI.create({
        name: newRoleName.trim(),
        description: newRoleDescription.trim(),
        permissions: [], // Empty permissions array - will be configured later
        isActive: true
      });

      showSuccess(`Role "${newRoleName}" created successfully. You can now assign permissions to it.`);

      // Reset modal state
      setShowCreateModal(false);
      setNewRoleName('');
      setNewRoleDescription('');

      // Refresh roles list and select the newly created role
      await fetchRolePermissions();
      setSelectedRole(response.data.id);
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to create role');
      console.error('Error:', error);
    } finally {
      setCreating(false);
    }
  };

  // Notification handlers
  const showSuccess = (message: string) => {
    setNotification({ message, type: 'success' });
    setTimeout(() => setNotification(null), 5000);
  };

  const showError = (message: string) => {
    setNotification({ message, type: 'error' });
    setTimeout(() => setNotification(null), 5000);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Permissions Management</h1>
        <p className="mt-2 text-sm text-gray-600">
          Configure role-based permissions for your organization.
        </p>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`mb-6 p-4 rounded-md ${
          notification.type === 'success'
            ? 'bg-green-50 border border-green-200'
            : 'bg-red-50 border border-red-200'
        }`}>
          <p className={`text-sm ${
            notification.type === 'success' ? 'text-green-800' : 'text-red-800'
          }`}>
            {notification.message}
          </p>
        </div>
      )}

      {/* Role Permissions */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="mb-6 flex items-end gap-4">
          <div className="flex-1">
            <label htmlFor="role-select" className="block text-sm font-medium text-gray-700 mb-2">
              Select Role
            </label>
            {availableRoles.length === 0 ? (
              <div className="text-sm text-gray-500 italic">
                You don't have permission to manage any roles.
              </div>
            ) : (
              <select
                id="role-select"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              >
                {availableRoles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.name.charAt(0).toUpperCase() + role.name.slice(1).replace('_', ' ')}
                    {role.roleType === 'CUSTOM' ? ' ⭐' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Role
          </button>
        </div>

        <p className="mb-4 text-sm text-amber-600">
          Note: Workflows, Environments, and Requested Items are system-protected resources and cannot be assigned to any role through this interface.
        </p>

        {currentUser?.roleType === 'GOD' && (
          <p className="mb-4 text-sm text-blue-600">
            Note: Super Admin role has all permissions by default and cannot be modified.
          </p>
        )}

        {availableRoles.find(r => r.id === selectedRole)?.roleType === 'CUSTOM' &&
          !isServiceAccountsCustomRole(
            availableRoles.find(r => r.id === selectedRole)?.roleType,
            availableRoles.find(r => r.id === selectedRole)?.name || ''
          ) && (
          <p className="mb-4 text-sm text-amber-600">
            Note: Custom roles cannot manage permissions (create/modify roles). The &quot;Permissions&quot; resource is hidden for custom roles.
          </p>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <>
            <PermissionMatrix
              permissions={rolePermissions[selectedRole] || []}
              onChange={(permissions) => {
                setRolePermissions(prev => ({
                  ...prev,
                  [selectedRole]: permissions
                }));
              }}
              disabled={saving}
              excludeResources={
                // Exclude "permissions" resource for custom roles
                // Exclude "workflows", "environments", and "requests" for ALL roles (no one can assign these permissions)
                (() => {
                  const excluded: string[] = ['workflows', 'environments', 'requests'];
                  const currentRole = availableRoles.find(r => r.id === selectedRole);

                  if (currentRole && !shouldShowPermissionsResourceForRole(currentRole)) {
                    excluded.push('permissions');
                  }

                  return excluded;
                })()
              }
            />

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => handleSaveRolePermissions(rolePermissions[selectedRole] || [])}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-300"
              >
                {saving ? 'Saving...' : 'Save Permissions'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Create Role Modal */}
      <Transition appear show={showCreateModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowCreateModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      Create Custom Role
                    </Dialog.Title>
                    <button
                      onClick={() => setShowCreateModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="mt-4 space-y-4">
                    <div>
                      <label htmlFor="role-name" className="block text-sm font-medium text-gray-700">
                        Role Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="role-name"
                        value={newRoleName}
                        onChange={(e) => setNewRoleName(e.target.value)}
                        placeholder="e.g., Project Manager"
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        autoFocus
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Role name must be unique
                      </p>
                    </div>

                    <div>
                      <label htmlFor="role-description" className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        id="role-description"
                        rows={3}
                        value={newRoleDescription}
                        onChange={(e) => setNewRoleDescription(e.target.value)}
                        placeholder="Describe the purpose and responsibilities of this role..."
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                      <p className="text-sm text-blue-800">
                        After creating the role, you can assign permissions to it using the permissions matrix below.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateModal(false);
                        setNewRoleName('');
                        setNewRoleDescription('');
                      }}
                      disabled={creating}
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateRole}
                      disabled={creating || !newRoleName.trim()}
                      className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {creating ? 'Creating...' : 'Create Role'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default PermissionsManagement;
